import {
  FormControl,
  FormLabel,
  InputGroup,
  Input,
  InputRightElement,
  Tooltip,
  Textarea,
  Box,
  Accordion,
  AccordionItem,
  AccordionButton,
  Flex,
  Text,
  AccordionIcon,
  AccordionPanel,
  FormErrorMessage,
} from "@chakra-ui/react";
import { FormikErrors, FormikTouched } from "formik";
import { HelpCircle } from "lucide-react";
import { EditAlertFormValues } from "pages/Alert/types";
import { useEffect, useRef, useState } from "react";

interface IAlertCustomTemplate {
  t: (key: string) => string;
  alertTitle: string;
  alertDescription: string;
  setAlertTitle: (value: string) => void;
  setAlertDescription: (value: string) => void;
  handleChange: (e: React.ChangeEvent<any>) => void;
  onAlertTemplateOpen: () => void;
  errors: FormikErrors<EditAlertFormValues>;
  touched: FormikTouched<EditAlertFormValues>;
}

export function AlertCustomTemplate({
  t,
  alertTitle,
  alertDescription,
  setAlertTitle,
  setAlertDescription,
  handleChange,
  onAlertTemplateOpen,
  errors,
  touched,
}: IAlertCustomTemplate) {
  const [accordionIndex, setAccordionIndex] = useState<number[]>([0]);
  const titleInputRef = useRef<HTMLInputElement>(null);

  const hasCustomTitleError = !!errors.customTitle && !!touched.customTitle;

  // Force accordion open and scroll to the title field when there's a validation error
  useEffect(() => {
    if (hasCustomTitleError) {
      setAccordionIndex([0]);
      // Wait for accordion to open, then scroll + focus
      setTimeout(() => {
        titleInputRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        setTimeout(() => titleInputRef.current?.focus(), 100);
      }, 150);
    }
  }, [hasCustomTitleError]);

  return (
    <Accordion
      color="black"
      bg="white"
      py={2}
      borderWidth={"1px"}
      borderColor={hasCustomTitleError ? "red.500" : "gray.200"}
      borderRadius={"md"}
      allowToggle
      index={accordionIndex}
      onChange={(index) => setAccordionIndex(index as number[])}
    >
      <AccordionItem border={0}>
        <AccordionButton
          _focus={{
            border: 0,
          }}
          _hover={{
            backgroundColor: "none",
          }}
        >
          <Flex flex={1} gap="12px">
            <Text fontSize="md" color="#086AA9">
              {t("customizeMessage")}
            </Text>
            <Text
              color="#414651"
              fontSize="12px"
              fontWeight="500"
              cursor="pointer"
              alignSelf={"center"}
              textDecoration={"underline"}
              _hover={{ textDecoration: "underline" }}
              onClick={(e) => {
                e.stopPropagation();
                onAlertTemplateOpen();
              }}
            >
              {t("viewMessage")}
            </Text>
          </Flex>
          <AccordionIcon color="#086AA9" />
        </AccordionButton>
        <AccordionPanel pt={5} px={4}>
          <FormControl mb={4} isRequired isInvalid={hasCustomTitleError}>
            <FormLabel color="#414651" fontWeight="500" fontSize="sm">
              {t("alertTitleDescribe")}
            </FormLabel>
            <InputGroup>
              <Input
                ref={titleInputRef}
                name="customTitle"
                placeholder={t("alertTitlePlaceholder")}
                value={alertTitle}
                onChange={(e) => {
                  handleChange(e);
                  setAlertTitle(e.target.value);
                }}
                pr="40px"
                maxLength={70}
              />
              <InputRightElement>
                <Tooltip
                  label={t("titleThatWillBe")}
                  bg="gray.700"
                  color="white"
                  fontSize="sm"
                  placement="top"
                  hasArrow
                >
                  <HelpCircle size="16px" color="#A4A7AE" />
                </Tooltip>
              </InputRightElement>
            </InputGroup>
            <FormErrorMessage mt={4}>
              {errors.customTitle
                ? t(errors.customTitle)
                : t("thisFieldRequired")}
            </FormErrorMessage>
          </FormControl>

          <FormControl
            isInvalid={!!errors.customDescription && touched.customDescription}
          >
            <FormLabel color="#414651" fontWeight="500" fontSize="sm">
              {t("alertDescription")}
            </FormLabel>
            <Box position="relative">
              <Textarea
                name="customDescription"
                placeholder={t("alertDescriptionPlaceholder")}
                value={alertDescription}
                onChange={(e) => {
                  handleChange(e);
                  setAlertDescription(e.target.value);
                }}
                rows={4}
                resize="vertical"
                pr="40px"
                maxLength={200}
              />
              <Box position="absolute" right="12px" top="12px">
                <Tooltip
                  label={t("describeTheImpact")}
                  bg="gray.700"
                  color="white"
                  fontSize="sm"
                  placement="top"
                  hasArrow
                  maxW="300px"
                >
                  <HelpCircle size="16px" color="#A4A7AE" />
                </Tooltip>
              </Box>
            </Box>
            <FormErrorMessage mt={4}>{t("thisFieldRequired")}</FormErrorMessage>
          </FormControl>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
}
